import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import ArticleCard from '@/components/feature/ArticleCard';
import VideoCard from '@/components/feature/VideoCard';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, videos, communityPosts, topics, faqItems } from '@/mocks/data';

const heroBg = 'https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20digital%20landscape%20with%20soft%20flowing%20shapes%20and%20geometric%20patterns%2C%20minimalist%20modern%20design%2C%20warm%20coral%20peach%20and%20cream%20color%20palette%2C%20smooth%20organic%20textures%2C%20clean%20ethereal%20atmosphere%2C%20no%20text%20no%20people%2C%20abstract%20art%20style&width=1600&height=800&seq=hero-bg-home&orientation=landscape';

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '挠脚心vk – 探索挠脚心vk的全方位内容聚合平台',
  description: '挠脚心vk是当前热门搜索关键词，本页汇聚挠脚心vk相关的视频、社区、内容资源与话题讨论。',
  url: 'https://naojiaoxinvk.com/',
  inLanguage: 'zh-CN',
  isPartOf: { '@type': 'WebSite', name: '挠脚心vk', url: 'https://naojiaoxinvk.com/' },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function HomePage() {
  return (
    <PageLayout>
      <SEOMeta
        title="挠脚心vk – 探索挠脚心vk的全方位内容聚合平台"
        description="挠脚心vk是当前热门搜索关键词，本页汇聚挠脚心vk相关的视频、社区、内容资源与话题讨论，帮助用户快速找到关于挠脚心vk的一切信息，满足不同用户的内容需求。"
        keywords="挠脚心vk,挠脚心vk视频,挠脚心vk社区,挠脚心相关内容,vk挠痒视频,挠脚心话题,挠脚心vk资源合集"
        canonicalUrl="https://naojiaoxinvk.com/"
      />
      <SchemaOrg data={webPageSchema} />
      <SchemaOrg data={faqSchema} />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
        <img src={heroBg} alt="挠脚心vk内容聚合平台" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
            探索 <span className="text-accent-400">挠脚心vk</span> 的全方位内容
          </h1>
          <p className="text-sm md:text-base text-background-200 max-w-2xl mb-8 leading-relaxed">
            汇聚挠脚心vk相关的视频、社区讨论、文章资讯与话题标签，帮助您快速找到关于挠脚心vk的一切信息
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/category/shipin" className="px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-play-circle-line mr-1.5"></i>浏览视频
            </Link>
            <Link to="/category/shequ" className="px-6 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-lg font-medium text-sm hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-chat-3-line mr-1.5"></i>加入社区
            </Link>
          </div>
        </div>
      </section>

      {/* Hot Topics Section */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
            <i className="ri-fire-line text-accent-500 mr-2"></i>热门话题
          </h2>
          <Link to="/category/huati" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            查看全部 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        <div className="flex flex-wrap gap-2">
          {topics.map((topic) => (
            <Link
              key={topic.slug}
              to={`/article/naojiaoxin-vk-2024-redian-pan`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary-100 text-secondary-900 rounded-full text-sm hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-1 h-1 rounded-full bg-accent-500"></span>
              {topic.name}
              <span className="text-secondary-600 text-xs">({topic.count})</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Videos Section */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
              <i className="ri-video-line text-primary-500 mr-2"></i>热门视频
            </h2>
            <Link to="/category/shipin" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              更多视频 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {videos.map((video) => (
              <VideoCard key={video.slug} {...video} />
            ))}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
            <i className="ri-chat-3-line text-accent-500 mr-2"></i>社区热帖
          </h2>
          <Link to="/category/shequ" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            全部讨论 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {communityPosts.map((post) => (
            <Link key={post.slug} to={`/article/naojiaoxin-vk-2024-redian-pan`} className="block group cursor-pointer">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5 hover:border-primary-200 transition-colors">
                <h3 className="text-sm font-medium text-foreground-900 mb-3 line-clamp-2 group-hover:text-primary-500 transition-colors">{post.title}</h3>
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span className="flex items-center gap-1"><i className="ri-user-line"></i>{post.author}</span>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1"><i className="ri-chat-1-line"></i>{post.replies}</span>
                    <span className="flex items-center gap-1"><i className="ri-heart-line"></i>{post.likes}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Articles Section */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
              <i className="ri-article-line text-primary-500 mr-2"></i>精选文章
            </h2>
            <Link to="/category/wenzhang" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              全部文章 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {articles.slice(0, 4).map((article) => (
              <ArticleCard key={article.slug} {...article} />
            ))}
          </div>
        </div>
      </section>

      {/* Resource Section */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
            <i className="ri-folder-zip-line text-accent-500 mr-2"></i>资源合集
          </h2>
          <Link to="/category/ziyuan" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            查看全部 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {[
            { icon: 'ri-video-line', title: '视频资源包', desc: '精选挠脚心vk视频汇总', color: 'bg-accent-100 text-accent-700' },
            { icon: 'ri-image-line', title: '图片素材库', desc: '高质量相关图片资源', color: 'bg-primary-100 text-primary-700' },
            { icon: 'ri-file-text-line', title: '文章文档集', desc: '深度文章与教程合集', color: 'bg-secondary-100 text-secondary-700' },
            { icon: 'ri-links-line', title: '链接导航站', desc: '常用资源链接汇总', color: 'bg-accent-100 text-accent-700' },
          ].map((item, i) => (
            <Link key={i} to="/article/naojiaoxin-ziyuan-heji-zhengli" className="block group cursor-pointer">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5 hover:border-primary-200 transition-colors text-center">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-lg flex items-center justify-center ${item.color}`}>
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1 group-hover:text-primary-500 transition-colors">{item.title}</h3>
                <p className="text-xs text-foreground-500">{item.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 text-center mb-8">
            常见问题
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqItems.map((item, i) => (
              <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between p-4 md:p-5 text-sm font-medium text-foreground-900 cursor-pointer">
                  {item.question}
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform ml-2"></i>
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">
          立即探索挠脚心vk的精彩世界
        </h2>
        <p className="text-sm text-foreground-600 mb-6 max-w-xl mx-auto">
          海量精彩内容等你发现，加入我们，成为挠脚心vk社区的一员
        </p>
        <Link to="/about" className="inline-flex items-center px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
          了解更多 <i className="ri-arrow-right-line ml-1.5"></i>
        </Link>
      </section>
    </PageLayout>
  );
}